import { BasicButton, PhoneNumber, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import env from "@/env.config";
import useProfile from "@/hooks/useProfile";
import { phoneNumber } from "@/redux/reducers/auth";
import { AccountSettingChangePhoneNumberFieldsSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { createFileRoute } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/change-phone-number",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const dispatch = useDispatch();
	const { profile: userData } = useProfile();

	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useForm<AccountSettingChangePhoneNumberFieldsSchema>({
		mode: "all",
		resolver: zodResolver(AccountSettingChangePhoneNumberFieldsSchema),
	});

	const onSubmit: SubmitHandler<AccountSettingChangePhoneNumberFieldsSchema> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		return dispatch(
			phoneNumber({
				phone_number: data.phone_number,
				callback: (res: { otp: string }) => {
					navigate({
						to: "/dashboard/accounts/settings/account/verify-phone-number",
						search: {
							phone_number: data.phone_number,
							...(!env.isProd ? { otp: res.otp } : {}),
						},
					});
				},
			}),
		);
	};

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Change Phone Number
					</Typography>
					<div>
						<Typography fontWeight={400} fontSize={14}>
							Your current phone number is
						</Typography>
						<Typography fontWeight={500} fontSize={14}>
							{userData?.phone_number ?? "-"}
						</Typography>
					</div>
					<Typography fontWeight={400} fontSize={14}>
						Enter your new phone number:
					</Typography>
					<Controller
						control={control}
						name="phone_number"
						defaultValue=""
						render={({ field, fieldState }) => (
							<PhoneNumber
								value={field.value}
								errorMessage={fieldState.error?.message}
								onChange={(e) =>
									field.onChange(`${e.dial_code}${e.phone_number}`)
								}
							/>
						)}
					/>
					<Typography fontWeight={400} fontSize={14}>
						You will receive a 4-digit code via SMS to your mobile phone.
					</Typography>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							Next
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
